import type { Location } from 'history'
import type { RootStateOrAny } from 'react-redux'
import type { EnterHook } from 'react-router'
import type { Store } from 'redux'

import authActions from 'actions/auth'
import config from 'config/routes'
import {
  getIsAuthenticated,
  getAccessToken,
  getHasRefreshCookie,
  getExpiresAt,
} from 'selectors/auth'

import { canUseWindow } from './browserEnvironment'

/**
 * Get origin url on the client
 * @private
 */
function getOrigin(): string {
  if (canUseWindow()) {
    return window.location.origin ? `${window.location.origin}/` : ''
  }

  return ''
}

export const addTargetToRedirect = ({
  target = false,
  location,
  redirectUrl = '/',
}: {
  target?: boolean
  location: Location
  redirectUrl?: string
}): string => {
  let redirectWithTarget

  if (target) {
    const origin = getOrigin()
    const path = location.search ? location.pathname + location.search : location.pathname

    let encodedUrl: string

    try {
      encodedUrl = encodeURIComponent(new URL(path, origin).toString())
    } catch (e) {
      encodedUrl = encodeURIComponent(origin + path)
    }

    redirectWithTarget = `${redirectUrl}?target=${encodedUrl}#login`
  }

  return redirectWithTarget || redirectUrl
}

/**
 * On Enter function, which prevent
 * access to not authenticated users
 * redirecting to the wished location
 */
export function checkValidSession(
  store: Store<RootStateOrAny, any>,
  redirectUrl?: string,
  target?: boolean,
): EnterHook {
  return async ({ location }, replace, next) => {
    const state = store.getState()
    const isAuthenticated = getIsAuthenticated(state)

    if (!isAuthenticated) {
      if (!canUseWindow()) {
        replace(addTargetToRedirect({ target, location, redirectUrl }))
      } else {
        const accessToken = getAccessToken(state)
        const hasRefreshCookie = getHasRefreshCookie(state)
        const expiresAt = getExpiresAt(state)

        if (!accessToken && !hasRefreshCookie) {
          replace(addTargetToRedirect({ target, location, redirectUrl }))
        } else {
          try {
            await store.dispatch(authActions.authValidate(accessToken, hasRefreshCookie, expiresAt))
          } catch (e) {
            replace(addTargetToRedirect({ target, location, redirectUrl }))
          }
        }
      }
    }

    if (next) {
      next()
    }
  }
}

/**
 * Check guest
 */
export function checkGuest(store: Store<RootStateOrAny, any>, redirectUrl = '/'): EnterHook {
  return (_, replace, next) => {
    const state = store.getState()
    const isAuthenticated = getIsAuthenticated(state)
    if (isAuthenticated) {
      replace(redirectUrl)
    }

    if (next) {
      next()
    }
  }
}

export function isPage(pathName: string, pageKey: keyof typeof config.client): boolean {
  return pathName === config.client[pageKey]
}

export function isOneOfPage(pathName: string, pageKeys: (keyof typeof config.client)[]): boolean {
  return pageKeys.some((pageKey) => isPage(pathName, pageKey))
}
